import React, { FC, useEffect, useState } from 'react';
import { Icon, SelectionMode, DetailsListLayoutMode, IconButton, TooltipHost, DirectionalHint } from '@fluentui/react';
import {
    Language,
    DetailsList,
    ColumnInfo,
    Label,
} from '@eta/components';
import { useTranslation } from 'react-i18next';
import { ITaxpayerRepresentationDto } from '../../Services/ClientProxy/clientProxy';
import { NumberOfShimmerLines, UIHelpers } from '../../helpers/UIHelpers';

import './TaxPayersGrid.scss';
import { Icons, StatusCodes } from '../../common/config/constants';
import RepresentationClaims from '../../TaxpayerProfile/components/RepresentationClaims';

interface TaxPayersGridProps {
    currentLanguage: Language;
    items: ITaxpayerRepresentationDto[] | undefined;
    onActionClick: (id: number) => void;
}
export const TaxPayersGrid: FC<TaxPayersGridProps> = (
    props: TaxPayersGridProps
) => {
    const { t } = useTranslation(['resources', 'myProfile', 'common']);
    const { currentLanguage: currentLanguageProps, items: itemsProps, onActionClick } = props;
    const [columns, setColumns] = useState<ColumnInfo[]>();
    const [currentLanguage, setCurrentLanguage] = useState<Language>(
        currentLanguageProps
    );
    const [items, setItems] = useState<ITaxpayerRepresentationDto[]>();

    useEffect(() => {
        setItems(itemsProps?.slice());
    }, [itemsProps, t]);

    useEffect(() => {
        if (currentLanguage.lang !== currentLanguageProps.lang) {
            setCurrentLanguage(currentLanguageProps);
            setItems(itemsProps?.slice());
        }
    }, [currentLanguageProps, items, itemsProps, currentLanguage, t]);

    const CheckProfileStatus = (status: string) => {
        return status === StatusCodes.Active
            ? t('myProfile:enumstatus_allowed')
            : t('constants.enumstatus_blockedAsUser');
    };

    useEffect(() => {
        const gridColumns: ColumnInfo[] = [
            {
                key: 'taxpayerNameEn',
                name: t('myProfile:taxpayerGrid_taxpayerNameEn'),
                fieldName: 'taxpayerNameEn',
                ariaLabel: ' Press to sort on TaxPayer Name',

                minWidth: 100,
                maxWidth: 250,

                isResizable: true,

                isSortedDescending: true,

                onRender: (item: ITaxpayerRepresentationDto) => {
                    return (
                        <div className="horizontal">
                            <Icon iconName="EMI" />
                            <span>{item.taxpayerNameEn}</span>
                        </div>
                    );
                },
            },
            {
                key: 'taxPayerNameAR',
                name: t('myProfile:taxpayerGrid_taxPayerNameAR'),
                fieldName: 'taxpayerNameAr',
                minWidth: 110,
                maxWidth: 250,
                isRowHeader: true,
                isResizable: true,
                isSorted: false,
                isSortedDescending: false,
                sortAscendingAriaLabel: 'Sorted A to Z',
                sortDescendingAriaLabel: 'Sorted Z to A',
                data: 'string',
                isPadded: true,
            },
            {
                key: 'role',
                name: t('myProfile:taxpayerGrid_role'),
                fieldName: 'role',
                allowSorting: true,
                minWidth: 100,
                maxWidth: 150,
                isRowHeader: true,
                isResizable: true,
                isSorted: false,
                isSortedDescending: false,
                sortAscendingAriaLabel: 'Sorted A to Z',
                sortDescendingAriaLabel: 'Sorted Z to A',
                data: 'string',
                isPadded: true,

                onRender: (item: ITaxpayerRepresentationDto) => {
                    const claimsCode =item.taxpayerRepresentationClaims?.map(x=> x.claimCode ?? '');
                    return (
                        <span>
                            {`${item.role} `}
                            <TooltipHost
                            directionalHint={DirectionalHint.rightBottomEdge}
                                tooltipProps={{
                                    onRenderContent: () => (
                                        <div>
                                        <Label className='roleLabelBold'>{item.role}</Label>
                                        <br/>
                                        <RepresentationClaims
                                            ClaimsCodes={claimsCode}
                                        />
                                        </div>
                                    ),
                                }}
                            >
                                <Icon iconName="Info" />
                            </TooltipHost>
                        </span>
                    );
                },
            },
            {
                key: 'allowedBranchesCount',
                name: t('TaxpayerUserGrid.NumberBranches'),
                fieldName: 'allowedBranchesCount',
                minWidth: 120,
                maxWidth: 130,
                isRowHeader: true,
                isResizable: true,
                isSorted: false,
                isSortedDescending: false,
                data: 'string',
                isPadded: true,

                onRender: (
                    item: ITaxpayerRepresentationDto
                ) => {
                    return (
                        <span>
                            {item.allBranches ? t('myProfile:allBranches') : item.allowedBranchesCount}
                        </span>
                    );
                },
            },
            {
                key: 'dateActivated',
                name: t('myProfile:taxpayerGrid_respentationFrom_To'),
                fieldName: 'dateActivated',
                allowSorting: true,
                minWidth: 150,
                maxWidth: 150,
                isRowHeader: true,
                isResizable: true,

                onRender: (item: ITaxpayerRepresentationDto) => {
                    return (
                        <span> 
                        {`${UIHelpers.getLocalizedDateTime('Date', item.dateActivated, currentLanguageProps.lang)}
                           - ${UIHelpers.getLocalizedDateTime('Date', item.dateDeactivated, currentLanguageProps.lang)}`}
                        </span>
                    );
                },
            },
            {
                key: 'status',
                name: t('myProfile:taxpayerGrid_status'),
                fieldName: 'status',
                minWidth: 120,
                maxWidth: 140,
                isResizable: false,
                data: 'string',
                isPadded: true,

                onRender: (item: ITaxpayerRepresentationDto) => {
                    return (
                        <span
                            className={
                                item.status.toString() === StatusCodes.Active &&
                                    item.userProfileStatus.toString() ===
                                    StatusCodes.Active
                                    ? 'green'
                                    : 'red'
                            }
                        >
                            {item.status.toString() === StatusCodes.Active
                                ? CheckProfileStatus(
                                    item.userProfileStatus.toString()
                                )
                                : t('myProfile:enumstatus_blocked')}
                        </span>
                    );
                },
            },
            {
                key: 'action',
                name: '',
                fieldName: '',
                minWidth: 0,
                maxWidth: 0,
                isRowHeader: true,
                isResizable: false,
                onRender: (item: ITaxpayerRepresentationDto) => {
                    return (
                        <span>
                            <IconButton
                                className="iconActions"
                                iconProps={{ iconName: Icons.MoreVertical }}
                                menuProps={{
                                    items: [
                                        {
                                            key: 'ViewDetails',
                                            text: t('form.actions.ViewDetails'),
                                            onClick: () =>
                                                onActionClick &&
                                                onActionClick(
                                                    item.taxpayerRepresentationID
                                                ),
                                        },
                                    ],
                                }}
                            />
                        </span>
                    );
                },
            },
        ];
        setColumns(gridColumns);
    }, [currentLanguageProps, t]);

    return (
        <DetailsList
            className="grid taxPayerGrid"
            columns={columns ?? []}
            items={items}
            shimmerLines={NumberOfShimmerLines}
            enableShimmer={!items}
            selectionMode={SelectionMode.none}
            layoutMode={DetailsListLayoutMode.justified}
            noItemsPlaceholder={<span>{t('common:NoRecords')} </span>}
            labels={{
                resultPerPage: t('common:nbItemPerPage'),
                totalRecord: t('common:totalRecord'),
            }}
            paginationSetting={{
                nbItemPerPage: 10,
                nbPageShown: 3 /* currentPage: 0, totalPage: 20 when managed externaly */,
            }}
            totalItemCount={items?.length}
        />
    );
};
